/**
 * 검색 결과 표시 SSoT — 조건 부합·접수 상태·지역 판단 라벨과 tone, 필터 선택지, 근거 출처. 공고 찾기·고객사 기준·
 * 지원사업 기준이 같은 표기를 쓴다. 컴포넌트 파일은 컴포넌트만 export(react-refresh)라 여기 둔다.
 */
import type { StatusTone } from '@/components/common/StatusTag';
import type { ApplyStatus, ConditionResult, Evidence } from '@/types/search';

import type { DeadlineRange, FinderSort, RegionVerdict } from '../types';

export const CONDITION_RESULT: Record<ConditionResult, { label: string; tone: StatusTone }> = {
  MET: { label: '충족', tone: 'success' },
  UNMET: { label: '조건 미충족', tone: 'neutral' },
  UNKNOWN: { label: '확인 필요', tone: 'warning' },
};

export const APPLY_STATUS: Record<ApplyStatus, { label: string; tone: StatusTone }> = {
  OPEN: { label: '접수중', tone: 'info' },
  UPCOMING: { label: '접수예정', tone: 'primary' },
  ROLLING: { label: '상시', tone: 'neutral' },
  UNKNOWN: { label: '시기 정보 없음', tone: 'warning' },
  CLOSED: { label: '마감', tone: 'neutral' },
};

/** 필터 선택지 순서 — 기본 목록 그룹 순서와 같다(접수중 → 접수예정 → 상시 → 시기 정보 없음 → 마감). 전체 상태 목록 SSoT. */
export const STATUS_OPTIONS: readonly ApplyStatus[] = [
  'OPEN',
  'UPCOMING',
  'ROLLING',
  'UNKNOWN',
  'CLOSED',
];

export const DEADLINE_OPTIONS: readonly { value: DeadlineRange; label: string }[] = [
  { value: 'D7', label: '7일 이내' },
  { value: 'D30', label: '30일 이내' },
  { value: 'D90', label: '90일 이내' },
];

export const SORT_OPTIONS: readonly { value: FinderSort; label: string }[] = [
  { value: 'LATEST', label: '최신순' },
  { value: 'DEADLINE', label: '마감 임박순' },
];

/** 지역 판단 표시(FR-013·015) — MATCH 는 표시하지 않는다(필터대로 보이는 정상 결과). */
export const REGION_VERDICT: Record<
  Exclude<RegionVerdict, 'MATCH'>,
  { label: string; tone: StatusTone }
> = {
  UNKNOWN: { label: '지역 확인 필요', tone: 'warning' },
  OTHER: { label: '다른 지역', tone: 'neutral' },
};

/** 근거 출처 링크 — 첨부는 원본 파일(PDF 는 해당 페이지 앵커), 본문은 공고 원문. */
export function evidenceHref(evidence: Evidence, originUrl: string | null): string | null {
  if (evidence.fileUrl) {
    const isPdf = (evidence.fileName ?? '').toLowerCase().endsWith('.pdf');
    return isPdf && evidence.pageFrom
      ? `${evidence.fileUrl}#page=${evidence.pageFrom}`
      : evidence.fileUrl;
  }
  return originUrl;
}

export function evidenceSourceLabel(evidence: Evidence): string {
  if (evidence.sourceKind === 'BODY' || !evidence.fileName) return '공고 본문';
  const page =
    evidence.pageFrom == null
      ? ''
      : evidence.pageTo && evidence.pageTo !== evidence.pageFrom
        ? ` p.${evidence.pageFrom}–${evidence.pageTo}`
        : ` p.${evidence.pageFrom}`;
  return `${evidence.fileName}${page}`;
}

/** 결과 카드 DOM id — 요약 인용 클릭 시 스크롤 대상. */
export function hitAnchorId(announcementId: number): string {
  return `search-hit-${announcementId}`;
}
